import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import MedicalServiceSelectorComponent from '../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent';
import { optionLabel } from '../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelector.Logic';
import { GuideField, GuideHelpText } from '../../../theme/components/GuideField';
import { GuideLinkButton } from '../../../theme/components/GuideButtons';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  ItemServiceOverride,
  SERVICE_CODE_MAX_LENGTH,
  SERVICE_NAME_MAX_LENGTH,
  validateServiceCreate,
} from '../../../functions/ProviderConsumptionImport.Review.Functions';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';

export type MedicalServiceChoiceMode = 'pick' | 'create';

export interface MedicalServiceChoiceDialogProps {
  open: boolean;
  /** Modo con que abre: elegir un servicio existente o crear uno nuevo. */
  mode: MedicalServiceChoiceMode;
  title: string;
  /** Contexto debajo del título (p. ej. el texto del ítem; se muestra como texto). */
  description?: string | null;
  /** Ofrece "Crear servicio nuevo" (en las correspondencias no). Por defecto true. */
  allowCreate?: boolean;
  /** Código y nombre con que se precompleta "Crear servicio nuevo" (los del ítem). */
  initialCreate?: { code?: string | null; name?: string | null };
  /** Servicio ya elegido (preselección del selector). */
  initialServiceId?: number | null;
  /** Clave de servicio de la fila: ofrece aplicar la elección a todos los ítems con la misma clave. */
  sameKey?: string | null;
  /** Guardando (correspondencias): deshabilita y muestra el progreso. */
  busy?: boolean;
  /** Error del guardado (correspondencias). */
  error?: unknown;
  onDismiss: () => void;
  onConfirm: (choice: ItemServiceOverride, options: { applyToSameKey: boolean }) => void;
}

/**
 * "Asignar servicio médico" / "Crear servicio nuevo" (spec 009, US6). Elegir reutiliza `MedicalServiceSelectorComponent`
 * en selección única (busca en el catálogo con `GetMedicalServicesPage`); crear pide código (opcional, ≤ 50) y nombre
 * (1 a 300) precompletados con los del ítem. Devuelve `{ medicalServiceId, label }` o `{ create: { code, name } }`.
 */
export const MedicalServiceChoiceDialog = ({
  open,
  mode: initialMode,
  title,
  description,
  allowCreate = true,
  initialCreate,
  initialServiceId,
  sameKey,
  busy,
  error,
  onDismiss,
  onConfirm,
}: MedicalServiceChoiceDialogProps) => {
  const { t } = useTranslation();
  const tr = (key: string, params?: Record<string, unknown>) => t(`providerConsumptionImport.review.service.dialog.${key}`, params) as string;
  const [mode, setMode] = useState<MedicalServiceChoiceMode>(initialMode);
  const [selected, setSelected] = useState<AvailableMedicalService | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [applyToSameKey, setApplyToSameKey] = useState(false);

  // Se reinicia al abrir en el mismo render (no en un efecto): si el primer cuadro abierto mostrara el otro modo, la
  // trampa de foco del diálogo tomaría ese campo y, al desaparecer, devolvería el foco al botón que lo abrió
  // ("Crear servicio nuevo" no dejaba escribir).
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setMode(allowCreate ? initialMode : 'pick');
      setSelected(undefined);
      setSelectedIds(initialServiceId ? [initialServiceId] : []);
      setCode((initialCreate?.code ?? '').slice(0, SERVICE_CODE_MAX_LENGTH));
      setName((initialCreate?.name ?? '').slice(0, SERVICE_NAME_MAX_LENGTH));
      setSubmitted(false);
      setApplyToSameKey(false);
    }
  }

  const errors = mode === 'create' ? validateServiceCreate({ code, name }) : {};
  const pickedId = selected?.id ?? (selectedIds.length > 0 ? selectedIds[0] : undefined);

  const confirm = () => {
    setSubmitted(true);
    if (mode === 'pick') {
      if (!pickedId) return;
      onConfirm({ medicalServiceId: pickedId, label: selected ? optionLabel(selected) : undefined }, { applyToSameKey });
      return;
    }
    if (errors.code || errors.name) return;
    onConfirm({ create: { code: code.trim() || null, name: name.trim() } }, { applyToSameKey });
  };

  const fieldError = (value?: string) => (submitted && value ? tr(`errors.${value}`) : undefined);

  return (
    <Dialog open={open} onOpenChange={(_event, data) => !data.open && !busy && onDismiss()}>
      <DialogSurface data-testid="pci-service-dialog" data-mode={mode}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <div className="pci-stack">
              {description && <GuideHelpText className="pci-review-name">{description}</GuideHelpText>}
              {error !== undefined && (
                <GuideMessageBar intent="error" layout="multiline" data-testid="pci-service-dialog-error">
                  <MessageBarBody>
                    <MessageBarTitle>{tr('saveError')}</MessageBarTitle>
                    {getApiErrorMessage(error)}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
              {mode === 'pick' ? (
                <>
                  <MedicalServiceSelectorComponent
                    MultiselectEnable={false}
                    MedicalServicesSelectedIds={selectedIds}
                    label={tr('serviceLabel')}
                    noteText={submitted && !pickedId ? tr('errors.serviceRequired') : tr('serviceHint')}
                    disabled={busy}
                    onSelectedServices={(services: AvailableMedicalService[] | undefined) => {
                      const first = services?.[0];
                      setSelected(first);
                      setSelectedIds(first ? [first.id] : []);
                    }}
                  />
                  {allowCreate && (
                    <GuideLinkButton size="small" onClick={() => setMode('create')} disabled={busy} data-testid="pci-service-dialog-to-create">
                      {tr('switchToCreate')}
                    </GuideLinkButton>
                  )}
                </>
              ) : (
                <>
                  <GuideField label={tr('code')} hint={tr('codeHint')} error={fieldError(errors.code)} counter={{ value: code.length, max: SERVICE_CODE_MAX_LENGTH }}>
                    <Input value={code} onChange={(_event, data) => setCode(data.value)} disabled={busy} data-testid="pci-service-dialog-code" />
                  </GuideField>
                  <GuideField label={tr('name')} required error={fieldError(errors.name)} counter={{ value: name.length, max: SERVICE_NAME_MAX_LENGTH }}>
                    <Input value={name} onChange={(_event, data) => setName(data.value)} disabled={busy} data-testid="pci-service-dialog-name" />
                  </GuideField>
                  <GuideLinkButton size="small" onClick={() => setMode('pick')} disabled={busy} data-testid="pci-service-dialog-to-pick">
                    {tr('switchToPick')}
                  </GuideLinkButton>
                </>
              )}
              {sameKey && (
                <Checkbox
                  checked={applyToSameKey}
                  onChange={(_event, data) => setApplyToSameKey(!!data.checked)}
                  label={tr('applyToSameKey', { key: sameKey })}
                  disabled={busy}
                  data-testid="pci-service-dialog-same-key"
                />
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onDismiss} disabled={busy} data-testid="pci-service-dialog-cancel">
              {tr('cancel')}
            </Button>
            <Button appearance="primary" onClick={confirm} disabled={busy} aria-busy={busy || undefined} data-testid="pci-service-dialog-confirm">
              {busy ? tr('saving') : mode === 'create' ? tr('confirmCreate') : tr('confirm')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default MedicalServiceChoiceDialog;
